import React, { useState } from 'react';
import { TextField } from '../inputs';
import { useField } from 'formik';
import NumberFormat from 'react-number-format';
import Downshift, { DownshiftProps, PropGetters } from 'downshift';
import MenuItem, { MenuItemProps } from '@material-ui/core/MenuItem';
import { Flex, Box, Text } from 'rebass';
import { Label, Checkbox } from '@rebass/forms';

interface CheckBoxProps {
  label: string;
  lightLabel?: boolean;
  name: string;
}

export const CheckBox = ({
  label,
  lightLabel,
  name = 'checkBox',
}: CheckBoxProps) => {
  const [field] = useField({
    name,
    type: 'checkbox',
  });

  return (
    <Label
      fontFamily="label"
      fontSize="16px"
      lineHeight={1.75}
      letterSpacing="0.5px"
      color={lightLabel ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)'}
      alignItems="center"
      css={{ cursor: 'pointer' }}
    >
      <Flex
        justifyContent="center"
        alignContent="center"
        height="20px"
        width="20px"
        mr="11px"
      >
        <Checkbox
          name="remember"
          height="20px"
          width="20px"
          m="auto"
          checked={field.checked}
          onBlur={field.onBlur}
          onChange={() => {
            field.onChange({
              target: {
                name,
                value: !field.checked,
              },
            });
          }}
          sx={{
            color: 'rgba(0, 0, 0, 0.54)',
            'input:focus ~ &': {
              color: 'rgba(0, 0, 0, 0.54)',
            },
            'input:checked ~ &': {
              bg: 'rgba(0, 0, 0, 0.54)',
              color: 'white',
              height: '20px',
              width: '20px',
            },
          }}
        />
      </Flex>
      {label}
    </Label>
  );
};

function maskEmailAddress(emailAddress: string) {
  function mask(str: string) {
    const strLen = str.length;
    if (strLen > 4) {
      return (
        str.substr(0, 1) +
        str.substr(1, strLen - 1).replace(/\w/g, '*') +
        str.substr(-1, 1)
      );
    }
    return str.replace(/\w/g, '*');
  }
  return emailAddress.replace(/([\w.]+)@([\w.]+)(\.[\w.]+)/g, function(
    _,
    p1,
    p2,
    p3
  ) {
    return mask(p1) + '@' + mask(p2) + p3;
  });
}

interface InputFieldProps {
  withButton?: boolean;
  label?: string;
  name?: string;
  onSubmitClicked?: (e: React.SyntheticEvent) => void;
  isSubmitButtonDisabled?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  submitStarted?: React.MutableRefObject<boolean>;
}

export const EmailField = ({
  withButton,
  onSubmitClicked,
  isSubmitButtonDisabled = true,
  isLoading,
  disabled,
  submitStarted,
}: InputFieldProps) => {
  const [field, meta, helper] = useField('email');

  const handleOnFocus = () => {
    if (
      meta.value === meta.initialValue &&
      (submitStarted as any)?.current !== true
    ) {
      helper.setValue('');
    }
  };

  return (
    <Box height="80px">
      <TextField
        name={field.name}
        buttonType="submit"
        label="Email"
        type="email"
        value={
          field.value !== meta.initialValue
            ? field.value
            : maskEmailAddress(field.value || '')
        }
        onChange={field.onChange}
        onBlur={field.onBlur}
        onFocus={handleOnFocus}
        withButton={withButton}
        helperText={meta.error}
        error={meta.touched && !!meta.error}
        onSubmitClicked={onSubmitClicked}
        isSubmitButtonDisabled={isSubmitButtonDisabled}
        isLoading={isLoading}
        disabled={disabled}
      />
    </Box>
  );
};

export const PasswordField = ({
  withButton,
  label = 'Password',
  name = 'password',
}: InputFieldProps) => {
  const [field, meta] = useField(name);
  return (
    <Box height="80px">
      <TextField
        label={label}
        name={field.name}
        type="password"
        value={meta.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        withButton={withButton}
        helperText={meta.error}
        error={meta.touched && !!meta.error}
      />
    </Box>
  );
};

export const FirstNameField = ({ withButton }: InputFieldProps) => {
  const [field, meta] = useField('firstName');
  return (
    <Box height="80px" width="100%">
      <TextField
        label="First"
        name={field.name}
        type="text"
        value={meta.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        withButton={withButton}
        helperText={meta.error}
        error={meta.touched && !!meta.error}
      />
    </Box>
  );
};

export const LastNameField = ({ withButton }: InputFieldProps) => {
  const [field, meta] = useField('lastName');
  return (
    <Box height="80px" width="100%">
      <TextField
        name={field.name}
        label="Last"
        type="text"
        value={meta.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        withButton={withButton}
        helperText={meta.error}
        error={meta.touched && !!meta.error}
      />
    </Box>
  );
};

interface NumberFormatCustomProps {
  inputRef: (instance: any) => void;
  onChange: (event: { target: { value: string; name: string } }) => void;
  name: string;
  value: string;
}

const WorkZipFormatCustom = (props: NumberFormatCustomProps) => {
  const { inputRef, onChange, name, ...other } = props;

  return (
    <NumberFormat
      {...other}
      name={name}
      getInputRef={inputRef}
      onValueChange={values => {
        //browser autofill caused infinite re-render
        //so I had to put a check in
        other.value !== values.value &&
          onChange({
            target: {
              name,
              value: values.value,
            },
          });
      }}
      isNumericString
      format="#####"
    />
  );
};

export const WorkZipField = ({ withButton }: InputFieldProps) => {
  const [field, meta] = useField('postalCode');
  return (
    <Box height="80px">
      <TextField
        name={field.name}
        label="Work Zip"
        type="text"
        value={meta.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        withButton={withButton}
        helperText={meta.error}
        error={meta.touched && !!meta.error}
        inputComponent={WorkZipFormatCustom}
        autoComplete="postal-code"
      />
    </Box>
  );
};

interface NumberFormatCustomProps {
  inputRef: (instance: any) => void;
  onChange: (event: { target: { value: string; name: string } }) => void;
  name: string;
  value: string;
}

const GraduationYearFormatCustom = (props: NumberFormatCustomProps) => {
  const { inputRef, onChange, name, ...other } = props;
  return (
    <NumberFormat
      {...other}
      name={name}
      getInputRef={inputRef}
      onValueChange={values => {
        //browser autofill caused infinite re-render
        //so I had to put a check in
        other.value !== values.value &&
          onChange({
            target: {
              name,
              value: values.value,
            },
          });
      }}
      isNumericString
      format="####"
    />
  );
};

export const GraduationYearField = ({ withButton }: InputFieldProps) => {
  const [field, meta] = useField('graduationYear');
  return (
    <Box height="80px">
      <TextField
        name={field.name}
        label="Graduation Year"
        type="text"
        value={meta.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        withButton={withButton}
        helperText={meta.error}
        error={meta.touched && !!meta.error}
        inputComponent={GraduationYearFormatCustom}
      />
    </Box>
  );
};

interface Suggestion {
  name: string;
}

interface RenderSuggestionProps {
  highlightedIndex?: number | null;
  index: number;
  itemProps: MenuItemProps<'div', { button?: never }>;
  selectedItem?: Suggestion['name'] | null;
  suggestion: Suggestion;
}

export const MedicalSchoolMenuItem = (
  suggestionProps: RenderSuggestionProps
) => {
  const {
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
  } = suggestionProps;
  const isHighlighted = highlightedIndex === index;
  const isSelected = (selectedItem || '').indexOf(suggestion.name) > -1;

  return (
    <MenuItem
      {...itemProps}
      selected={isHighlighted}
      component="div"
      style={{
        fontWeight: isSelected ? 500 : 400,
        paddingTop: 11,
        paddingBottom: 11,
        overflowX: 'scroll',
      }}
    >
      <Text fontFamily="label" fontSize="16px" color="#1c273f">
        {suggestion.name}
      </Text>
    </MenuItem>
  );
};

export interface MedicalSchoolFieldChildrenProps {
  getItemProps: PropGetters<string>['getItemProps'];
  highlightedIndex: DownshiftProps<number | null>['highlightedIndex'];
  selectedItem: DownshiftProps<string>['selectedItem'];
  inputValue: string;
}

interface MedicalSchoolFieldProps {
  withButton?: boolean;
  children: (props: MedicalSchoolFieldChildrenProps) => React.ReactNode;
}

export const MedicalSchoolField = ({
  withButton,
  children,
}: MedicalSchoolFieldProps) => {
  const [field, meta] = useField('medicalSchool');
  const [value, setValue] = useState('');

  const handleStateChange = (changes: DownshiftProps<'onStateChange'>) => {
    if (changes.hasOwnProperty('selectedItem')) {
      const selectedItem = changes.selectedItem ? changes.selectedItem : '';
      field.onChange({
        target: {
          name: field.name,
          value: selectedItem,
        },
      });
      setValue(selectedItem);
    } else if (changes.hasOwnProperty('inputValue')) {
      const inputValue = changes.inputValue ? changes.inputValue : '';
      field.onChange({
        target: {
          name: field.name,
          value: inputValue,
        },
      });
      setValue(inputValue);
    }
  };

  return (
    <Downshift onStateChange={handleStateChange} selectedItem={value}>
      {({
        clearSelection,
        getInputProps,
        getItemProps,
        getMenuProps,
        highlightedIndex,
        inputValue,
        isOpen,
        selectedItem,
      }) => {
        const downshiftInputProps = getInputProps({
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.value === '') {
              clearSelection();
            }
          },
          onBlur: field.onBlur,
        });

        const {
          onBlur,
          onChange,
          onFocus,
          ...restInputProps
        } = downshiftInputProps;

        return (
          <div>
            <Box height="80px">
              <TextField
                name={field.name}
                label="Medical School"
                type="text"
                value={inputValue}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={
                  onFocus as (
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >
                  ) => void
                }
                withButton={withButton}
                helperText={meta.error}
                error={meta.touched && !!meta.error}
                autoComplete="off"
                inputProps={restInputProps}
              />
            </Box>
            <div
              style={{
                position: 'relative',
                bottom: '24px',
              }}
            >
              {isOpen && inputValue && inputValue.length > 4 ? (
                <Box
                  {...getMenuProps()}
                  maxHeight="191px"
                  width="100%"
                  css={{
                    border: 'solid 1px rgba(19, 40, 76, 0.6)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'absolute',
                    zIndex: 2,
                    transition: 'opacity .1s ease',
                  }}
                  bg="white"
                >
                  {children({
                    getItemProps,
                    highlightedIndex,
                    selectedItem,
                    inputValue: inputValue,
                  })}
                </Box>
              ) : null}
            </div>
          </div>
        );
      }}
    </Downshift>
  );
};

export const SpecialtyIdSelect = () => {
  const [field, meta] = useField('specialtyId');
  const [value, setValue] = useState<string | null>(null);

  const handleChange = (event: any) => {
    field.onChange({
      target: {
        name: field.name,
        value:
          items[items.findIndex(item => item.name === event.target.value)].id,
      },
    });
    setValue(event.target.value);
  };

  return (
    <TextField
      select
      name={field.name}
      label={value ? 'Specialty' : 'Practice Specialty'}
      type="text"
      value={value}
      onChange={handleChange}
      helperText={meta.error}
      error={meta.touched && !!meta.error}
      autoComplete="off"
    >
      {items.map(item => {
        return (
          <MenuItem key={item.id} value={item.name}>
            <Text fontFamily="label" fontSize="16px" color="#1c273f">
              {item.name}
            </Text>
          </MenuItem>
        );
      })}
    </TextField>
  );
};

const items = [
  { id: 2, name: 'Allergy and Immunology' },
  { id: 3, name: 'Anesthesiology' },
  { id: 19, name: 'Cardiology' },
  { id: 7, name: 'Dermatology' },
  { id: 9, name: 'Emergency Medicine' },
  { id: 23, name: 'Endocrinology/Diabetes' },
  { id: 11, name: 'Family or General Practice' },
  { id: 25, name: 'Gastroenterology' },
  { id: 28, name: 'Hematology and Oncology' },
  { id: 29, name: 'Infectious Disease' },
  { id: 18, name: 'Internist or Critical Care' },
  { id: 30, name: 'Nephrology' },
  { id: 31, name: 'Neurology' },
  { id: 48, name: 'Obstetrics and Gynecology' },
  { id: 49, name: 'Ophthalmology' },
  { id: 50, name: 'Orthopedics' },
  { id: 59, name: 'Otolaryngology/ENT' },
  { id: 144, name: 'Pain Management' },
  { id: 62, name: 'Pathology' },
  { id: 73, name: 'Pediatrics' },
  { id: 204, name: 'Physical Medicine and Rehabilitation' },
  { id: 94, name: 'Psychiatry' },
  { id: 102, name: 'Radiology' },
  { id: 39, name: 'Rheumatology' },
  { id: 112, name: 'Urology' },
];
